import { Component, effect, OnDestroy, signal, viewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { FormBuilder } from '@angular/forms';
import { SubscriptionComponent } from '../../../shared/helpers/subscription.component';
import { debounceTime } from 'rxjs/operators';
import config from '../../../../config/config';
import { SearchStore } from './search.store';
import { SearchInputComponent } from '../../../../components/kit/input/text-input/search-input.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { Tab, TabList, Tabs } from '@openng/optimus-ui/tabs';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { LoadingComponent } from '../../../../components/kit/loading/loading.component';
import { LinkButtonComponent } from '../../../../components/kit/button/link-button.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { SearchResultComponent } from './search-result/search-result.component';

@Component({
    selector: 'app-search',
    templateUrl: 'search.component.html',
    styleUrl: 'search.component.scss',
    providers: [SearchStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        Dialog,
        TranslatePipe,
        SearchInputComponent,
        Tabs,
        TabList,
        Tab,
        AsyncPipe,
        NgTemplateOutlet,
        LoadingComponent,
        LinkButtonComponent,
        EmptyStateComponent,
        SearchResultComponent,
    ],
})
export class SearchComponent extends SubscriptionComponent implements OnDestroy {

    protected readonly store = inject(SearchStore);
    private readonly layoutService = inject(LayoutService);
    private readonly fb = inject(FormBuilder);
    public inputElement = viewChild<SearchInputComponent>('search');

    protected activeIndex = signal<number>(0);
    protected activeIndexPages = ['all', ...this.store.searchTabs, 'history'];
    protected loadingNext = false;
    protected form = this.fb.group({
        search: this.fb.control(''),
    });

    private timeout?: ReturnType<typeof setTimeout>;

    constructor() {
        super();

        effect(() => {
            this.activeIndex();
            this.lazyLoad();
        });

        this.subs.push(
            this.store.searchTabsSubj.subscribe((tabs) => {
                this.activeIndexPages = ['all', ...tabs, 'history'];
            }),
            this.form.controls.search.valueChanges.pipe(
                debounceTime(config.debounceTime),
            ).subscribe((search) => {
                if (!search?.length) return;
                this.store.clear();
                this.loadingNext = false;
                this.store.nextPage(this.activeIndexPages[this.activeIndex()], search || '');
            }),
            this.store.data.results$.subscribe(() => {
                this.loadingNext = false;
            }),
            this.store.data.historyResults$.subscribe(() => {
                this.loadingNext = false;
            }),
        );
    }

    get visible(): boolean {
        return this.layoutService.state.searchBarActive;
    }

    set visible(_val: boolean) {
        this.layoutService.state.searchBarActive = _val;
    }

    public hide() {
        this.visible = false;
    }

    public override ngOnDestroy(): void {
        if (this.timeout) clearTimeout(this.timeout);
    }

    protected lazyLoad(): void {
        if (!this.form.value.search?.length) return;
        this.store.nextPage(this.activeIndexPages[this.activeIndex()], this.form.value.search || '', true);
    }

    protected loadNext(entity: string = 'all'): void {
        this.loadingNext = true;
        this.store.nextPage(entity, this.form.value.search || '');
    }

    protected focusOnInput(): void {
        setTimeout(() => this.inputElement()?.focusInput(), 200);
    }
}
